/**
 * Menu tree fixture for the Assisted eKYC showcase build.
 * Specified in contracts-1 §2.1 (tree), §2.4 (nodes) and §2.5 (per-role visibility).
 *
 * Shape notes, derived from how the layout actually consumes this data:
 *
 *  - `menuUrl` MUST be a plain string. `app.menuitem.component.ts` binds
 *    `[routerLink]="[!item.subMenus ? item.menuUrl : router.url]"` — it wraps the value in an
 *    array itself, so an array here would produce a nested array and break navigation.
 *
 *  - Leaf nodes MUST NOT declare `subMenus` at all (not even `[]`). The same binding treats any
 *    truthy `subMenus` as "this is a group", and a group navigates to `router.url` instead of its
 *    own link — an empty array would silently make the leaf unclickable.
 */

export type MockRole = 'MAKER' | 'BRANCH_CHECKER' | 'COGB_CHECKER';

export interface MockMenuNode {
  menuName: string;
  menuUrl?: string;
  icon?: string;
  position: number;
  permissionAsString?: string;
  subMenus?: MockMenuNode[];
}

const BROWSE = 'BROWSE';
const MAKER_PERMS = 'BROWSE,CREATE,EDIT';
const CHECKER_PERMS = 'BROWSE,AUTHORIZE';

/** Routes that exist in this app today and can be linked directly. */
const ROUTE = {
  // Home points at the showcase landing rather than /account-portal/workflow. Login navigates to
  // the first navigable node in this tree, and `workflow` calls the real backend — which is not
  // mocked in chunk 1, so every login would land on an error toast. Repoint when it is mocked.
  home: '/account-portal/assisted-ekyc/home',
  pendingActions: '/account-portal/workflow',
  /** Audit Log → All Admin Log. */
  auditLogAllAdmin: '/admin/audit-log',
  assistedEkyc: '/account-portal/assisted-ekyc',
  fdrAccountOpening: '/account-portal/fdr-account-opening-requests',
  appSettings: '/account-portal/app-settings',
  /** Nodes whose real page does not exist yet park here rather than 404. */
  stub: '/account-portal/empty-landing',
};

/** All four list variants share one component; the role comes from route data. */
type ListVariant = 'all' | 'maker' | 'checker' | 'cogb';

function variantsFor(role: MockRole): ListVariant[] {
  switch (role) {
    case 'MAKER':
      return ['all', 'maker'];
    case 'BRANCH_CHECKER':
      return ['all', 'checker'];
    case 'COGB_CHECKER':
      return ['all', 'cogb'];
  }
}

function permsFor(variant: ListVariant): string {
  if (variant === 'maker') return MAKER_PERMS;
  if (variant === 'checker' || variant === 'cogb') return CHECKER_PERMS;
  return BROWSE;
}

const VARIANT_LABEL: Record<ListVariant, string> = {
  all: '(All)',
  maker: '(Maker)',
  checker: '(Checker)',
  cogb: '(COGB)',
};

/**
 * Builds one "…Process (All|Maker|Checker|COGB)" group for the given section.
 *
 * `hideAllFrom` drops the cross-branch (All) queue for the listed roles — it is a supervisory
 * view, so a Maker does not get it on every section.
 */
function requestGroup(
  role: MockRole,
  opts: {
    menuName: string;
    icon: string;
    position: number;
    segment: string;
    labelPrefix: string;
    hideAllFrom?: MockRole[];
  },
): MockMenuNode {
  const variants = variantsFor(role).filter(
    (variant) => !(variant === 'all' && opts.hideAllFrom?.includes(role)),
  );

  return {
    menuName: opts.menuName,
    icon: opts.icon,
    position: opts.position,
    subMenus: variants.map((variant, index) => ({
      menuName: `${opts.labelPrefix} ${VARIANT_LABEL[variant]}`,
      menuUrl: `${ROUTE.assistedEkyc}/${opts.segment}/${variant}`,
      position: index + 1,
      permissionAsString: permsFor(variant),
    })),
  };
}

/**
 * Service Enrollment is a single queue shared by every role — no (All) view, and no per-role
 * variants. Everyone lands on the same list, so the node carries no variant suffix.
 */
function serviceEnrollmentGroup(position: number): MockMenuNode {
  return {
    menuName: 'Service Enrollment Request',
    icon: 'pi pi-fw pi-credit-card',
    position,
    subMenus: [
      {
        menuName: 'Service Enrollment Requests Process',
        menuUrl: `${ROUTE.assistedEkyc}/service-enrollment/maker`,
        position: 1,
        permissionAsString: BROWSE,
      },
    ],
  };
}

function fdrAccountOpeningGroup(role: MockRole, position: number): MockMenuNode {
  return {
    menuName: 'FDR/DPS Accounts',
    icon: 'pi pi-fw pi-wallet',
    position,
    subMenus: [
      {
        menuName: 'FDR/DPS Account Opening',
        position: 1,
        subMenus: variantsFor(role).filter((variant) => variant !== 'all').map((variant, index) => ({
          menuName: `FDR/DPS Account Opening Request Process ${VARIANT_LABEL[variant]}`,
          menuUrl: `${ROUTE.fdrAccountOpening}/${variant}`,
          position: index + 1,
          permissionAsString: permsFor(variant),
        })),
      },
    ],
  };
}

/**
 * The KYC Upgrade section labels its "all" node differently from the others —
 * "Assisted eKYC Simplified Requests (All)" rather than "… Upgrade Request Process (All)".
 * That wording comes straight from the requested tree, so it is preserved verbatim.
 *
 * Hidden from every role for now: only the navigation entry is suppressed — the
 * /assisted-ekyc/kyc-upgrade/* routes stay reachable by direct URL. Flip
 * SHOW_KYC_UPGRADE back to true to restore the section.
 */
const SHOW_KYC_UPGRADE = false;

function kycUpgradeGroup(role: MockRole, position: number): MockMenuNode | null {
  if (!SHOW_KYC_UPGRADE) return null;
  return {
    menuName: 'KYC Upgrade',
    icon: 'pi pi-fw pi-arrow-up-right',
    position,
    subMenus: variantsFor(role).map((variant, index) => ({
      menuName:
        variant === 'all'
          ? 'Assisted eKYC Simplified Requests (All)'
          : `Assisted eKYC Upgrade Request Process ${VARIANT_LABEL[variant]}`,
      menuUrl: `${ROUTE.assistedEkyc}/kyc-upgrade/${variant}`,
      position: index + 1,
      permissionAsString: permsFor(variant),
    })),
  };
}

/**
 * Configuration is hidden from every role for now (it was COGB-only, contracts-1 §2.5).
 * Only the navigation entry is suppressed — the /assisted-ekyc/configuration/* routes
 * stay reachable by direct URL. Flip SHOW_CONFIGURATION back to true to restore it.
 */
const SHOW_CONFIGURATION = false;

function configurationGroup(role: MockRole, position: number): MockMenuNode | null {
  if (!SHOW_CONFIGURATION || role !== 'COGB_CHECKER') return null;
  const perms = MAKER_PERMS;
  const pages = [
    { menuName: 'Risk Grading', segment: 'risk-grading' },
    { menuName: 'Risk Grading Score', segment: 'risk-grading-score' },
    { menuName: 'Transaction Profile', segment: 'transaction-profile' },
    { menuName: 'Transaction Profile Limit', segment: 'transaction-profile-limit' },
  ];
  return {
    menuName: 'Configuration',
    icon: 'pi pi-fw pi-cog',
    position,
    subMenus: pages.map((page, index) => ({
      menuName: page.menuName,
      menuUrl: `${ROUTE.assistedEkyc}/configuration/${page.segment}`,
      position: index + 1,
      permissionAsString: perms,
    })),
  };
}

export function buildMenuTree(role: MockRole): MockMenuNode[] {
  const tree: MockMenuNode[] = [
    {
      menuName: 'Home',
      menuUrl: ROUTE.home,
      icon: 'pi pi-fw pi-home',
      position: 1,
      permissionAsString: BROWSE,
    },
    {
      menuName: 'Audit Log',
      icon: 'pi pi-fw pi-book',
      position: 4,
      subMenus: [
        // Open to every role — no permission gate on the menu or the route.
        { menuName: 'All Admin Log', menuUrl: ROUTE.auditLogAllAdmin, position: 1, permissionAsString: BROWSE },
      ],
    },
    {
      menuName: 'Settings',
      icon: 'pi pi-fw pi-sliders-h',
      position: 5,
      subMenus: [
        { menuName: 'App Settings', menuUrl: ROUTE.appSettings, position: 1, permissionAsString: BROWSE },
      ],
    },
    fdrAccountOpeningGroup(role, 7),
    {
      menuName: 'Assisted eKYC',
      icon: 'pi pi-fw pi-id-card',
      position: 8,
      subMenus: [
        requestGroup(role, {
          menuName: 'Assisted eKYC Request',
          icon: 'pi pi-fw pi-file-edit',
          position: 1,
          segment: 'requests',
          labelPrefix: 'Assisted eKYC Request Process',
          // Every role works from its own queue only — the cross-branch (All) view
          // is hidden across the board. Drop a role here to give it back.
          hideAllFrom: ['MAKER', 'BRANCH_CHECKER', 'COGB_CHECKER'],
        }),
        serviceEnrollmentGroup(2),
        kycUpgradeGroup(role, 3),
        configurationGroup(role, 4),
      ].filter((node): node is MockMenuNode => node !== null),
    },
  ];

  return tree;
}

/** Where each role lands immediately after login (contracts-2 §A, "Landing page by role"). */
export function landingRouteFor(role: MockRole): string {
  switch (role) {
    case 'MAKER':
      return `${ROUTE.assistedEkyc}/requests/maker`;
    case 'BRANCH_CHECKER':
      return `${ROUTE.assistedEkyc}/requests/checker`;
    case 'COGB_CHECKER':
      return `${ROUTE.assistedEkyc}/requests/cogb`;
  }
}
